'use client';

import { useMemo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { format } from 'date-fns';

import {
  GameListHeader,
  GamesViewSkeleton,
  LiveGameCard,
  SocketDisconnectedBanner,
  SportsGameCard,
} from '@/components/';
import { useLiveSportsSocket, useSportsMarketSocket } from '@/hooks';
import { useLiveEvents, useStartingSoonEvents } from '@/services/query';
import { EventResponse } from '@/types';
import { capitalizeAllWords, esportsLast } from '@/utils';

interface LiveViewProps {
  onTeamClick: (event: EventResponse, teamIndex: 1 | 2, isPreviousMarket?: boolean) => void;
  onPropTradeClick?: (marketId: string, outcomeId: string, isBuy: boolean) => void;
  columnVisibility: { hasMoneyline: boolean; hasSpread: boolean; hasTotal: boolean };
  activeOutcomeId?: string;
  searchQuery?: string;
}

export const LiveView = ({
  onTeamClick,
  onPropTradeClick,
  columnVisibility,
  activeOutcomeId,
  searchQuery,
}: LiveViewProps) => {
  const { liveData } = useLiveSportsSocket();
  const { data: rawLiveEvents, isLoading: isLiveLoading } = useLiveEvents({ search: searchQuery });

  // Fetch Starting Soon Events
  const { data: rawStartingSoonEvents, isLoading: isStartingSoonLoading } = useStartingSoonEvents({
    search: searchQuery,
  });

  // Filter events to ensure data integrity — esports events go last.
  // We require both competitors (homeTeam/awayTeam) so the card can render, but
  // NOT a seriesSlug: individual-competitor sports (tennis, UFC, golf) may lack
  // one, and dropping them here is exactly why the Live board was missing the
  // bulk of what Polymarket shows. Events without a series fall back to the
  // "other" group in the grouping step below.
  const liveEvents = (rawLiveEvents || [])
    .filter((e) => !!e.homeTeam && !!e.awayTeam)
    .sort(esportsLast);

  const startingSoonEvents = (rawStartingSoonEvents || []).filter(
    (e) => !!e.homeTeam && !!e.awayTeam
  );

  // Combine events to feed into the socket hook for subscription
  const initialEvents = useMemo(
    () => [...liveEvents, ...startingSoonEvents],
    [liveEvents, startingSoonEvents]
  );
  const { isConnected: isMarketSocketConnected } = useSportsMarketSocket(initialEvents);

  // Helper to enrich event with socket data
  const enrichEvent = (event: EventResponse) => {
    if (!event.gameId) return event;
    const socketUpdate = liveData[event.gameId.toString()];
    if (!socketUpdate) return event;

    const newScore = socketUpdate.score || event.score;
    const newPeriod = socketUpdate.period || event.period;
    const newIsLive = socketUpdate.live ?? event.isLive;

    if (newScore === event.score && newPeriod === event.period && newIsLive === event.isLive) {
      return event;
    }

    return {
      ...event,
      score: newScore,
      period: newPeriod,
      isLive: newIsLive,
    };
  };

  // Group Live Events by seriesSlug — esports groups appear last because events are pre-sorted.
  // Fall back to the series title (individual-competitor sports like tennis may not carry a
  // seriesSlug) so those still group under their series header instead of a catch-all "other".
  const groupedLiveEvents = useMemo(() => {
    if (!liveEvents) return {};
    return liveEvents.reduce((acc: Record<string, EventResponse[]>, event) => {
      const slug = event.seriesSlug || event.series?.[0]?.title || 'other';
      if (!acc[slug]) acc[slug] = [];
      acc[slug].push(event);
      return acc;
    }, {});
  }, [liveEvents]);

  // Group Starting Soon by startTime and then seriesSlug
  const groupedStartingSoonEvents = useMemo(() => {
    if (!startingSoonEvents) return {};
    const groupedByTime: Record<string, Record<string, EventResponse[]>> = {};

    startingSoonEvents.forEach((event) => {
      const startTime = event.startTime
        ? format(new Date(event.startTime), 'EEE, MMMM d')
        : 'Unknown';
      const seriesSlug = event?.series?.[0]?.title || 'other';

      if (!groupedByTime[startTime]) groupedByTime[startTime] = {};
      if (!groupedByTime[startTime][seriesSlug]) groupedByTime[startTime][seriesSlug] = [];

      groupedByTime[startTime][seriesSlug].push(event);
    });

    return groupedByTime;
  }, [startingSoonEvents]);

  if (isLiveLoading || isStartingSoonLoading) {
    return <GamesViewSkeleton />;
  }

  const renderLiveGame = (event: EventResponse) => {
    const enriched = enrichEvent(event);
    return (
      <SportsGameCard
        key={event.id}
        event={enriched}
        onTeamClick={onTeamClick}
        onPropTradeClick={onPropTradeClick}
        columnVisibility={columnVisibility}
        activeOutcomeId={activeOutcomeId}
      />
    );
  };

  const renderLiveGroup = ([slug, events]: [string, EventResponse[]]) => {
    const renderedLiveGames = events.map(renderLiveGame);

    return (
      <Box key={slug} sx={{ mb: 4 }}>
        <GameListHeader
          date={capitalizeAllWords(slug.replace(/-/g, ' '))}
          columnVisibility={columnVisibility}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{renderedLiveGames}</Box>
      </Box>
    );
  };

  const renderStartingSoonGame = (event: EventResponse) => (
    <LiveGameCard
      key={event.id}
      event={event}
      isStartingSoon={true}
      onTeamClick={onTeamClick}
      onPropTradeClick={onPropTradeClick}
      columnVisibility={columnVisibility}
      activeOutcomeId={activeOutcomeId}
    />
  );

  const renderStartingSoonSeries = ([slug, events]: [string, EventResponse[]]) => {
    const renderedStartingSoonGames = events.map(renderStartingSoonGame);

    return (
      <Box key={slug} sx={{ mb: 3 }}>
        <GameListHeader
          date={capitalizeAllWords(slug.replace(/-/g, ' '))}
          columnVisibility={columnVisibility}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {renderedStartingSoonGames}
        </Box>
      </Box>
    );
  };

  const renderStartingSoonGroup = ([time, seriesGroups]: [
    string,
    Record<string, EventResponse[]>,
  ]) => {
    const renderedStartingSoonSeriesList =
      Object.entries(seriesGroups).map(renderStartingSoonSeries);

    return (
      <Box key={time} sx={{ mb: 4 }}>
        <Typography sx={{ mb: 2, fontWeight: 700, fontSize: '1.25rem' }}>{time}</Typography>
        {renderedStartingSoonSeriesList}
      </Box>
    );
  };

  const renderedLiveGroupsList = Object.entries(groupedLiveEvents).map(renderLiveGroup);
  const renderedStartingSoonGroupsList =
    Object.entries(groupedStartingSoonEvents).map(renderStartingSoonGroup);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Socket disconnected banner */}
      {isMarketSocketConnected === false && <SocketDisconnectedBanner />}

      {/* Live Section */}
      {Object.keys(groupedLiveEvents).length > 0 && (
        <>
          <Box>{renderedLiveGroupsList}</Box>

          <Divider />
        </>
      )}

      {/* Starting Soon Section */}
      {Object.keys(groupedStartingSoonEvents).length > 0 && (
        <Box>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
            Starting Soon
          </Typography>
          {renderedStartingSoonGroupsList}
        </Box>
      )}
    </Box>
  );
};
